import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import Collapse from '@material-ui/core/Collapse';
import Tooltip from '@material-ui/core/Tooltip';

import {
    Box,
    CircularProgress,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    withStyles,
} from "@material-ui/core";
import axios from "axios";

import {formatDate, formatMoney} from "../../lib/helperFuncs.jsx";

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogTitle: {
        minWidth: "8rem",
    },
});

const useStyles = makeStyles({
    noPaymentsMsg: {
        padding: "1rem",
    },
});

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose } = props;

    return (
        <MuiDialogTitle disableTypography>
            <Typography variant="h5" className={classes.dialogTitle}>
                {children}
            </Typography>
            {onClose && (
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            )}
        </MuiDialogTitle>
    );
});

const PaymentsDialog = (props) => {
    const { payments, paymentTypeNames, refunds } = { ...props };
    const classes = useStyles();
    const [loading, setLoading] = useState(false);

    const [expandedPayments, setExpandedPayments] = useState({});

    const toggleExpand = (paymentId) => {
        setExpandedPayments(prev => ({
            ...prev,
            [paymentId]: !prev[paymentId]
        }));
    };

    useEffect(() => {
        if (payments.length > 0) {
            props.initAction();
        }
    }, []);

    const formattedPayments = paymentTypeNames
        ? payments.map((payment) => {
              paymentTypeNames.forEach((name) => {
                  if (name.id === payment.payment_method_id) {
                      payment["method_name"] = name.name;
                  }
              });
            return payment;
        })
        : null;

    const paymentsForTable = formattedPayments?.map((payment) => {
        const hasFiscalData = payment?.fiscal_document_number ||
            payment?.fiscal_storage_number ||
            payment?.fiscal_attribute;
        const isExpanded = expandedPayments[payment.id];

        return (
            <React.Fragment key={payment.id}>
                <TableRow>
                    <TableCell>
                        <span style={{ whiteSpace: 'nowrap' }}>{formatDate(payment.paid_on)}</span>
                    </TableCell>
                    <TableCell>{formatMoney(payment.amount)}</TableCell>
                    <TableCell>{payment.method_name}</TableCell>
                    <TableCell>{formatPaymentStatus(payment.status)}</TableCell>
                    <TableCell>{payment.manager}</TableCell>
                    <TableCell>
                        {hasFiscalData && (
                            <Tooltip
                                title={isExpanded ? "Скрыть фискальные данные" : "Развернуть список фискальных данных"}
                                placement="top"
                                arrow
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => toggleExpand(payment.id)}
                                    aria-label={isExpanded ? 'Скрыть детали' : 'Показать детали'}
                                >
                                    {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                            </Tooltip>
                        )}
                    </TableCell>
                </TableRow>

                {hasFiscalData && (
                    <TableRow>
                        <TableCell style={{ padding: 0 }} colSpan={6}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <div style={{
                                    padding: 16,
                                    backgroundColor: '#f5f5f5',
                                    borderTop: '1px solid #ddd'
                                }}>
                                    {payment?.fiscal_storage_number && (
                                        <div style={{ marginBottom: 8 }}>
                                            <strong style={{ color: '#666' }}>ФД: </strong>
                                            <span>{payment.fiscal_storage_number}</span>
                                        </div>
                                    )}
                                    {payment?.fiscal_document_number && (
                                        <div style={{ marginBottom: 8 }}>
                                            <strong style={{ color: '#666' }}>ФН: </strong>
                                            <span>{payment.fiscal_document_number}</span>
                                        </div>
                                    )}
                                    {payment?.fiscal_attribute && (
                                        <div>
                                            <strong style={{ color: '#666' }}>ФП: </strong>
                                            <span>{payment.fiscal_attribute}</span>
                                        </div>
                                    )}

                                    {payment?.fiscal_storage_number && payment?.fiscal_document_number && payment?.fiscal_attribute && (
                                        <div style={{ marginTop: 8 }}>
                                            <a
                                                href={`https://consumer.1-ofd.ru/ticket?fn=${payment.fiscal_storage_number}&i=${payment.fiscal_document_number}&fp=${payment.fiscal_attribute}&n=1`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    color: '#1976d2',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Посмотреть чек в ОФД
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </Collapse>
                        </TableCell>
                    </TableRow>
                )}
            </React.Fragment>
        );
    });

    const refundsForTable = refunds.map((refund) => (
        <TableRow key={refund.id}>
            <TableCell>
                <span style={{whiteSpace: 'nowrap'}}>{formatDate(refund.created_at)}</span>
            </TableCell>
            <TableCell>-{formatMoney(refund.amount)}</TableCell>
            <TableCell></TableCell>
            <TableCell>Возвращено</TableCell>
            <TableCell></TableCell>
        </TableRow>
    ))

    const paymentTable =
        payments.length > 0 && paymentsForTable ? (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Дата Оплаты</TableCell>
                        <TableCell>Сумма оплаты</TableCell>
                        <TableCell>Способ Оплаты</TableCell>
                        <TableCell>Результат</TableCell>
                        <TableCell>Менеджер</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>{paymentsForTable}{refundsForTable}</TableBody>
            </Table>
        ) : payments.length > 0 && !paymentsForTable ? (
            <Box className={classes.noPaymentsMsg}>
                <CircularProgress size={30} />
            </Box>
        ) : (
            <Typography className={classes.noPaymentsMsg}>
                Оплат по данному бронированию не производилось
            </Typography>
        );

    const handleOnlinePayments = () => {
        setLoading(true);
        axios
            .get(
                `/manager/data/reservations/sync_online_payments/${props.reservation.hotel_id}/${props.reservation.id}`,
            )
            .then(() => {
                props.reLoad(props.reservation.id);
            })
            .catch((error) => console.error(error));
        setLoading(false);
    };

    return (
        <Dialog
            open={props.openPaymentsDialog}
            onClose={() => props.setOpenPaymentsDialog(false)}
            scroll={"body"}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
            maxWidth={false}
        >
            <DialogTitle
                id="customized-dialog-title"
                onClose={() => props.setOpenPaymentsDialog(false)}
            >
                Оплаты
                {/*{loading || props.loading*/}
                {/*    ?*/}
                {/*    <CircularProgress/>*/}
                {/*    :*/}
                {/*    <div className={style.dialogButtons}>*/}
                {/*        <button onClick={() => handleOnlinePayments()} className={style.checkButton}>Проверить онлайн*/}
                {/*            оплаты*/}
                {/*        </button>*/}
                {/*    </div>*/}
                {/*}*/}
            </DialogTitle>
            {paymentTable}
        </Dialog>
    );
};

//Helper funcs

/**
 * Format payment status
 *
 * @param {String} status
 */

const formatPaymentStatus = (status) => {
    switch (status) {
        case "paid":
            return "Оплачено";
        case "refunded":
            return "Возврат";
    }
};

PaymentsDialog.propTypes = {
    payments: PropTypes.array,
    openPaymentsDialog: PropTypes.bool,
    setOpenPaymentsDialog: PropTypes.func,
    initAction: PropTypes.func,
    paymentTypeNames: PropTypes.array,
};

export default PaymentsDialog;
